import { useState, useEffect, useCallback } from 'react'
import DOMPurify from 'dompurify'
import { useUser } from '@clerk/clerk-react'
import {
  getAdminEmails,
  approveEmail,
  rejectEmail,
  editEmail,
  queueEmail,
  draftEmail,
  type EmailQueueItem,
  type EmailDraft,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

type FilterTab = 'all' | 'pending' | 'sent' | 'rejected'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    pending: 'badge-warning',
    sent: 'badge-success',
    rejected: 'badge-error',
    failed: 'bg-red-900/30 text-red-300',
    approved: 'badge-signal',
  }
  return (
    <span className={`${map[status] ?? 'badge badge-steel'}`}>
      {status}
    </span>
  )
}

// ─── Draft Modal ──────────────────────────────────────────────────────────────

interface DraftModalProps {
  draft: EmailDraft
  onQueue: (draft: EmailDraft) => Promise<void>
  onClose: () => void
  queuing: boolean
}

function DraftModal({ draft, onQueue, onClose, queuing }: DraftModalProps) {
  const [subject, setSubject] = useState(draft.subject)
  const [bodyHtml, setBodyHtml] = useState(draft.body_html)
  const [bodyText, setBodyText] = useState(draft.body_text)

  async function handleQueue() {
    await onQueue({ ...draft, subject, body_html: bodyHtml, body_text: bodyText })
  }

  return (
    <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-obsidian border border-carbon rounded w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-carbon flex items-center justify-between">
          <h2 className="text-h2 text-chalk">AI Draft</h2>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1 leading-none">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <div className="text-body-sm text-steel mb-1">To</div>
            <div className="text-body text-chalk">
              {draft.to_name ? `${draft.to_name} <${draft.to_email}>` : draft.to_email}
            </div>
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Subject</label>
            <input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body text-chalk focus:outline-none focus:border-signal"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Body (HTML)</label>
            <textarea
              value={bodyHtml}
              onChange={e => setBodyHtml(e.target.value)}
              rows={8}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk font-mono focus:outline-none focus:border-signal resize-y"
            />
          </div>

          <div>
            <label className="block text-body-sm text-steel mb-1">Body (Plain text)</label>
            <textarea
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              rows={5}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk font-mono focus:outline-none focus:border-signal resize-y"
            />
          </div>
        </div>

        <div className="p-6 border-t border-carbon flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-body-sm text-steel hover:text-chalk border border-carbon rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleQueue}
            disabled={queuing}
            className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {queuing ? 'Queuing…' : 'Queue for Approval'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Reject Dialog ────────────────────────────────────────────────────────────

interface RejectDialogProps {
  onConfirm: (reason: string) => void
  onCancel: () => void
}

function RejectDialog({ onConfirm, onCancel }: RejectDialogProps) {
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-obsidian border border-carbon rounded w-full max-w-md p-6">
        <h3 className="text-h3 text-chalk mb-4">Reject Email</h3>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Reason (optional)"
          rows={3}
          className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal resize-none mb-4"
        />
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-body-sm text-steel hover:text-chalk border border-carbon rounded">Cancel</button>
          <button
            onClick={() => onConfirm(reason)}
            className="px-4 py-2 text-body-sm bg-red-600 text-white rounded hover:bg-red-700"
          >
            Reject
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Email Card ───────────────────────────────────────────────────────────────

interface EmailCardProps {
  email: EmailQueueItem
  onApprove: (id: string) => Promise<void>
  onReject: (id: string, reason?: string) => Promise<void>
  onEdit: (id: string, fields: Partial<EmailQueueItem>) => Promise<void>
}

function EmailCard({ email, onApprove, onReject, onEdit }: EmailCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editSubject, setEditSubject] = useState(email.subject)
  const [editBodyHtml, setEditBodyHtml] = useState(email.body_html)
  const [showReject, setShowReject] = useState(false)
  const [acting, setActing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = (email.body_text ?? stripHtml(email.body_html)).slice(0, 200)

  async function handleApprove() {
    setActing(true)
    setError(null)
    try {
      await onApprove(email.id)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setActing(false)
    }
  }

  async function handleRejectConfirm(reason: string) {
    setShowReject(false)
    setActing(true)
    setError(null)
    try {
      await onReject(email.id, reason || undefined)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setActing(false)
    }
  }

  async function handleSaveEdit() {
    setActing(true)
    setError(null)
    try {
      await onEdit(email.id, { subject: editSubject, body_html: editBodyHtml })
      setEditing(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setActing(false)
    }
  }

  return (
    <>
      {showReject && (
        <RejectDialog onConfirm={handleRejectConfirm} onCancel={() => setShowReject(false)} />
      )}

      <div className="bg-obsidian border border-carbon rounded p-5 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-body font-semibold text-chalk truncate">
              {email.to_name ? `${email.to_name} <${email.to_email}>` : email.to_email}
            </div>
            {editing ? (
              <input
                value={editSubject}
                onChange={e => setEditSubject(e.target.value)}
                className="mt-1 w-full bg-void border border-carbon rounded px-2 py-1 text-body-sm text-chalk focus:outline-none focus:border-signal"
              />
            ) : (
              <div className="text-body-sm text-steel mt-0.5 truncate">{email.subject}</div>
            )}
          </div>
          <div className="shrink-0">{statusBadge(email.status)}</div>
        </div>

        {/* Meta */}
        <div className="flex flex-wrap gap-3 text-footnote text-steel">
          <span>Queued {formatDate(email.queued_at)}</span>
          {email.template_type && <span>· {email.template_type}</span>}
          {email.reviewed_at && <span>· Reviewed {formatDate(email.reviewed_at)}</span>}
          {email.resend_message_id && (
            <span className="text-green-400">· Sent ✓</span>
          )}
          {email.rejection_reason && (
            <span className="text-red-400">· Reason: {email.rejection_reason}</span>
          )}
        </div>

        {/* Body preview / edit */}
        {editing ? (
          <div>
            <label className="block text-footnote text-steel mb-1">Body HTML</label>
            <textarea
              value={editBodyHtml}
              onChange={e => setEditBodyHtml(e.target.value)}
              rows={6}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-footnote text-chalk font-mono focus:outline-none focus:border-signal resize-y"
            />
          </div>
        ) : (
          <div>
            <p className="text-body-sm text-steel leading-relaxed">
              {preview}{preview.length >= 200 ? '…' : ''}
            </p>
            {expanded && (
              <div
                className="mt-3 p-3 bg-void rounded border border-carbon text-body-sm text-steel"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body_html ?? '') }}
              />
            )}
            <button
              onClick={() => setExpanded(v => !v)}
              className="mt-1 text-footnote text-signal hover:underline"
            >
              {expanded ? 'Collapse' : 'View full email'}
            </button>
          </div>
        )}

        {/* Error */}
        {error && <div className="text-footnote text-red-400 bg-red-900/20 rounded px-3 py-2">{error}</div>}

        {/* Actions */}
        {email.status === 'pending' && (
          <div className="flex flex-wrap gap-2 pt-1">
            {editing ? (
              <>
                <button
                  onClick={handleSaveEdit}
                  disabled={acting}
                  className="px-3 py-1.5 text-footnote bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50"
                >
                  {acting ? 'Saving…' : 'Save Changes'}
                </button>
                <button
                  onClick={() => { setEditing(false); setEditSubject(email.subject); setEditBodyHtml(email.body_html) }}
                  className="px-3 py-1.5 text-footnote text-steel hover:text-chalk border border-carbon rounded"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="px-3 py-1.5 text-footnote text-steel hover:text-chalk border border-carbon rounded transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={handleApprove}
                  disabled={acting}
                  className="px-3 py-1.5 text-footnote bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {acting ? 'Sending…' : '✓ Approve & Send'}
                </button>
                <button
                  onClick={() => setShowReject(true)}
                  disabled={acting}
                  className="px-3 py-1.5 text-footnote bg-red-600/20 text-red-400 rounded hover:bg-red-600/30 disabled:opacity-50 transition-colors"
                >
                  Reject
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminEmailQueue() {
  const { user, isLoaded } = useUser()
  const [emails, setEmails] = useState<EmailQueueItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<FilterTab>('all')
  const [draft, setDraft] = useState<EmailDraft | null>(null)
  const [draftLeadId, setDraftLeadId] = useState('')
  const [draftTemplateType, setDraftTemplateType] = useState<string>('outbound_intro')
  const [draftInstructions, setDraftInstructions] = useState('')
  const [drafting, setDrafting] = useState(false)
  const [draftError, setDraftError] = useState<string | null>(null)
  const [queuing, setQueuing] = useState(false)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  const loadEmails = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getAdminEmails(filter === 'all' ? undefined : filter)
      setEmails(data.emails)
      setTotal(data.total)
    } catch {
      // silent
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    if (isAdmin) void loadEmails()
  }, [isAdmin, loadEmails])

  async function handleApprove(id: string) {
    const updated = await approveEmail(id)
    setEmails(prev => prev.map(e => e.id === id ? updated : e))
  }

  async function handleReject(id: string, reason?: string) {
    const updated = await rejectEmail(id, reason)
    setEmails(prev => prev.map(e => e.id === id ? updated : e))
  }

  async function handleEdit(id: string, fields: Partial<EmailQueueItem>) {
    const updated = await editEmail(id, fields)
    setEmails(prev => prev.map(e => e.id === id ? updated : e))
  }

  async function handleDraft() {
    if (!draftLeadId.trim()) return
    setDrafting(true)
    setDraftError(null)
    try {
      const result = await draftEmail({
        lead_id: draftLeadId.trim(),
        template_type: draftTemplateType,
        custom_instructions: draftInstructions || undefined,
      })
      setDraft(result)
    } catch (err) {
      setDraftError((err as Error).message)
    } finally {
      setDrafting(false)
    }
  }

  async function handleQueueDraft(d: EmailDraft) {
    setQueuing(true)
    try {
      const queued = await queueEmail({
        lead_id: d.lead_id,
        to_email: d.to_email,
        to_name: d.to_name ?? undefined,
        from_email: d.from_email,
        reply_to: d.reply_to ?? undefined,
        subject: d.subject,
        body_html: d.body_html,
        body_text: d.body_text,
        template_type: d.template_type,
      })
      setDraft(null)
      setDraftLeadId('')
      setDraftInstructions('')
      // Prepend to list and switch to pending tab
      setFilter('pending')
      setEmails(prev => [queued, ...prev])
      setTotal(t => t + 1)
    } finally {
      setQueuing(false)
    }
  }

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  const TABS: FilterTab[] = ['all', 'pending', 'sent', 'rejected']

  return (
    <>
      {draft && (
        <DraftModal
          draft={draft}
          onQueue={handleQueueDraft}
          onClose={() => setDraft(null)}
          queuing={queuing}
        />
      )}

      <div className="flex-1 p-8 text-chalk">
        <div className="max-w-3xl space-y-8">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-h1 text-chalk">Email Queue</h1>
              <p className="text-body-sm text-steel mt-1">Review, edit, and approve outbound emails before sending.</p>
            </div>
            <button
              onClick={loadEmails}
              className="text-body-sm text-signal hover:underline"
            >
              Refresh
            </button>
          </div>

          {/* AI Draft Panel */}
          <div className="bg-obsidian border border-carbon rounded p-5">
            <h2 className="text-h3 text-chalk mb-4">✦ Draft with AI</h2>
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 tablet:grid-cols-2">
                <div>
                  <label className="block text-footnote text-steel mb-1">Lead ID (UUID)</label>
                  <input
                    value={draftLeadId}
                    onChange={e => setDraftLeadId(e.target.value)}
                    placeholder="paste lead UUID…"
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal font-mono"
                  />
                </div>
                <div>
                  <label className="block text-footnote text-steel mb-1">Template type</label>
                  <select
                    value={draftTemplateType}
                    onChange={e => setDraftTemplateType(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                  >
                    <option value="outbound_intro">Outbound intro</option>
                    <option value="follow_up">Follow-up</option>
                    <option value="demo_invite">Demo invite</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-footnote text-steel mb-1">Extra context (optional)</label>
                <input
                  value={draftInstructions}
                  onChange={e => setDraftInstructions(e.target.value)}
                  placeholder="e.g. they were at NeurIPS, mention computer vision benchmark"
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                />
              </div>
              {draftError && (
                <div className="text-footnote text-red-400 bg-red-900/20 rounded px-3 py-2">{draftError}</div>
              )}
              <button
                onClick={handleDraft}
                disabled={drafting || !draftLeadId.trim()}
                className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {drafting ? 'Drafting…' : 'Generate Draft'}
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-3">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`text-body-sm capitalize px-3 py-1 rounded transition-colors ${
                  filter === tab ? 'bg-signal text-white' : 'text-steel hover:text-chalk'
                }`}
              >
                {tab}
              </button>
            ))}
            <span className="ml-auto text-body-sm text-steel">{total} total</span>
          </div>

          {/* Email list */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div
                data-testid="loading-spinner"
                className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-signal"
              />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-steel text-body">No emails{filter !== 'all' ? ` with status "${filter}"` : ''}.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {emails.map(email => (
                <EmailCard
                  key={email.id}
                  email={email}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          )}

        </div>
      </div>
    </>
  )
}
